"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";

import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";

import comCSS from "./HeroBlock.module.scss";
import ResponsiveImage from "../../ui/responsive-image/ResponsiveImage";

const MEDIA_MODES = {
  VIDEO: "video",
  IMAGE: "image",
};

const HeroBlock = ({ className, data = {} }) => {
  const [scrollLevel, setScrollLevel] = useState(1);
  const wrapperRef = useRef();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const onScroll = () => {
        const panel = document.querySelector(".after-hero-page-wrap-unique");

        if (!panel || !wrapperRef.current) return null;
        const height = wrapperRef.current.clientHeight;
        const panelTop = panel.getBoundingClientRect().top;

        const scrollLevel = Math.max(Math.min(panelTop / height, 1), 0);
        setScrollLevel(scrollLevel);
      };

      window.addEventListener("scroll", onScroll, false);
      window.addEventListener("resize", onScroll, false);

      // Call handler right away so state gets updated with initial window size
      onScroll();

      return () => {
        window.removeEventListener("scroll", onScroll, false);
        window.removeEventListener("resize", onScroll, false);
      };
    }
  }, []);

  const { mediaMode, bgImage, mediaModeMobile, bgImageMobile } = data || {};

  const getHeaderContentTransform = (change) => {
    const max = 80;
    const translateY = (change - 1) * max;
    return `translate3d(0px, ${translateY}px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)`;
  };

  return (
    <header
      className={cn(className, comCSS.wrapper, "section_hero_full-height")}
      ref={wrapperRef}
    >
      <div className="padding-global z-1">
        <div className="container-large">
          <div className="padding-section-custom-hero-hp">
            <div className="w-layout-grid header1_component">
              <div
                className="header_content"
                style={{
                  willChange: "opacity, transform",
                  opacity: scrollLevel,
                  transform: getHeaderContentTransform(scrollLevel),
                  transformStyle: "preserve-3d",
                }}
              >
                <div>
                  <div className="margin-bottom margin-xsmall">
                    <div className="text-note is-white hide-tablet">
                      {convertHTMLtoJSX(data?.preTitle)}
                    </div>
                    <div className="text-note is-white hide-desktop">
                      {convertHTMLtoJSX(data?.preTitleMobile)}
                    </div>
                  </div>
                  <div className="margin-bottom margin-small">
                    <div className="max-width-hero-h1">
                      <div className="margin-bottom margin-xsmall">
                        <div className="text-shadow hide-tablet">
                          {convertHTMLtoJSX(data?.title)}
                        </div>
                        <div className="text-shadow hide-desktop">
                          {convertHTMLtoJSX(data?.titleMobile)}
                        </div>
                      </div>
                      <div className="max-width-small">
                        <div className="text-size-medium hide-tablet">
                          {convertHTMLtoJSX(data?.text)}
                        </div>
                        <div className="text-size-medium hide-desktop">
                          {convertHTMLtoJSX(data?.textMobile)}
                        </div>
                      </div>
                      <div className="margin-top margin-small">
                        <div className="button-group">
                          <ButtonLink
                            data={data?.secondaryButton}
                            className="is-icon hide-tablet"
                            isPhoneIcon
                          />
                          <ButtonLink
                            data={data?.secondaryButtonMobile}
                            className="is-icon hide-desktop"
                            isPhoneIcon
                          />
                          <ButtonLink
                            data={data?.button}
                            className="is-secondary is-alternate is-icon hide-tablet"
                          />
                          <ButtonLink
                            data={data?.buttonMobile}
                            className="is-secondary is-alternate is-icon hide-desktop"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="header-bottom-section">
                  {!data?.scrollDownButton?.hide ? (
                    <a
                      href={data?.scrollDownButton?.url}
                      className="scroll-down-wrap w-inline-block hide-tablet"
                    >
                      <div
                        className={cn(
                          comCSS.scrollDownLottie,
                          "scroll-down-lottie"
                        )}
                        data-w-id="7d1fa991-0757-8073-f527-664c6796853b"
                        data-animation-type="lottie"
                        data-loop="1"
                        data-direction="1"
                        data-autoplay="1"
                        data-is-ix2-target="0"
                        data-renderer="svg"
                        data-default-duration="1.6333333333333333"
                        data-duration="0"
                      >
                        <div className={comCSS.scrollDownDot} />
                      </div>
                      <div>
                        {convertHTMLtoJSX(data?.scrollDownButton?.text)}
                      </div>
                    </a>
                  ) : null}
                  {!data?.scrollDownButtonMobile.hide ? (
                    <a
                      href={data?.scrollDownButtonMobile?.url}
                      className="scroll-down-wrap w-inline-block hide-desktop"
                    >
                      <div
                        className={cn(
                          comCSS.scrollDownLottie,
                          "scroll-down-lottie"
                        )}
                        data-w-id="7d1fa991-0757-8073-f527-664c6796853b"
                        data-animation-type="lottie"
                        data-loop="1"
                        data-direction="1"
                        data-autoplay="1"
                        data-is-ix2-target="0"
                        data-renderer="svg"
                        data-default-duration="1.6333333333333333"
                        data-duration="0"
                      >
                        <div className={comCSS.scrollDownDot} />
                      </div>
                      <div>
                        {convertHTMLtoJSX(data?.scrollDownButtonMobile?.text)}
                      </div>
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="background-hero-wrap hide-tablet"
        style={{
          backgroundColor: data.bgColor || "",
        }}
      >
        {mediaMode === MEDIA_MODES.VIDEO ? (
          <div
            id="hero-fullscreen-video"
            className="hero-fullscreen-video w-background-video w-background-video-atom"
            data-autoplay="true"
            data-loop="true"
            data-wf-ignore="true"
          >
            <video
              id="7d1fa991-0757-8073-f527-664c67968540-video"
              autoPlay
              loop
              playsInline
              style={{
                backgroundImage: `url(/responsive-image?url=${data?.videoPoster})`,
              }}
              muted
              data-wf-ignore
              data-object-fit="cover"
              className={cn(
                comCSS.heroFullscreenVideo,
                comCSS.video,
                "hero-fullscreen-video"
              )}
              crossOrigin="anonymous"
              controlsList="play timeline volume nofullscreen nodownload noremoteplayback noplaybackrate"
              preload="auto"
            >
              <source src={data?.videoSrc} data-wf-ignore="true" />
            </video>
          </div>
        ) : null}
        {mediaMode === MEDIA_MODES.IMAGE ? (
          <div className={cn(comCSS.bgImgWrapper)}>
            <ResponsiveImage
              src={bgImage?.src}
              alt={bgImage?.alt}
              sizes="(min-width: 992px) 100vw, 5px"
              width={2560}
              height={800}
              className={comCSS.bgImg}
            />
          </div>
        ) : null}
        <div className="hero-background-gradient-overlay"></div>
        <div
          className="hero-background-scroll-overlay"
          style={{ willChange: "opacity", opacity: 1 - scrollLevel }}
        />
      </div>

      <div
        className="background-hero-wrap hide-desktop"
        style={{
          backgroundColor: data?.bgColorMobile || "",
        }}
      >
        {mediaModeMobile === MEDIA_MODES.VIDEO ? (
          <div
            id="hero-fullscreen-video"
            className="hero-fullscreen-video w-background-video w-background-video-atom"
            data-autoplay="true"
            data-loop="true"
            data-wf-ignore="true"
          >
            <video
              id="7d1fa991-0757-8073-f527-664c67968540-video"
              autoPlay
              loop
              playsInline
              style={{
                backgroundImage: `url(/responsive-image?url=${data?.videoPosterMobile})`,
              }}
              muted
              data-wf-ignore
              data-object-fit="cover"
              className={cn(
                comCSS.heroFullscreenVideo,
                comCSS.video,
                "hero-fullscreen-video"
              )}
              controlsList="play timeline volume nofullscreen nodownload noremoteplayback noplaybackrate"
              crossOrigin="anonymous"
              preload="auto"
            >
              <source src={data?.videoSrcMobile} data-wf-ignore="true" />
            </video>
          </div>
        ) : null}
        {mediaModeMobile === MEDIA_MODES.IMAGE ? (
          <div className={cn(comCSS.bgImgWrapper)}>
            <ResponsiveImage
              src={bgImageMobile?.src}
              alt={bgImageMobile?.alt}
              sizes="(min-width: 992px) 5px, 100vw"
              width={992}
              height={800}
              className={comCSS.bgImg}
            />
          </div>
        ) : null}
        <div className="hero-background-gradient-overlay"></div>
        <div
          className="hero-background-scroll-overlay"
          style={{ willChange: "opacity", opacity: 1 - scrollLevel }}
        />
      </div>
    </header>
  );
};

HeroBlock.propTypes = {
  data: PropTypes.shape({
    preTitle: PropTypes.string,
    preTitleMobile: PropTypes.string,
    title: PropTypes.string,
    titleMobile: PropTypes.string,
    text: PropTypes.string,
    textMobile: PropTypes.string,
    button: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    secondaryButton: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    buttonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    secondaryButtonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    scrollDownButton: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
    }),
    scrollDownButtonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
    }),
    videoPoster: PropTypes.string,
    videoSrc: PropTypes.string,
    videoPosterMobile: PropTypes.string,
    videoSrcMobile: PropTypes.string,
    mediaMode: PropTypes.oneOf(["video", "image"]), // Restricts media mode options (consider removing the empty string option)
    bgImage: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    images: PropTypes.arrayOf(PropTypes.shape({})), // Consider adding more specific validation for image objects within the array
    mediaModeMobile: PropTypes.oneOf(["video", "image"]), // Restrict mobile media mode options
    bgImageMobile: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    imagesMobile: PropTypes.arrayOf(PropTypes.shape({})), // Consider adding more specific validation for mobile image objects within the array
    bgColor: PropTypes.string,
    bgColorMobile: PropTypes.string,
  }).isRequired,
};

export default HeroBlock;
